import {
  autoUpdate,
  FloatingFocusManager,
  FloatingPortal,
  flip,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
  useRole
} from '@floating-ui/react';
import { useId } from 'react';
import { useIntl } from 'react-intl';
import { CustomThemeProvider, Tag } from 'sonata-react-components';

import { groupingMessages } from '../groupingMessages';
import { GroupsMenuList } from './GroupsMenuList';
import { getGroupTagStyle } from './groupTagStyle';
import { SingleGroupCard } from './SingleGroupCard';
import { useHoverOpen } from './useHoverOpen';

import './groupsMenu.scss';

import type { GroupFamily, GroupView } from '../../domain/grouping.types';

/** Time the menu stays open after the pointer leaves the tag or the menu, so it can travel between them. */
export const GROUPS_MENU_CLOSE_DELAY_MS = 150;

export interface GroupsMenuProps {
  family: GroupFamily;
  /** Visible groups of the item, in display order. */
  groups: GroupView[];
  /** Opens "Edit Groups" on the detail of this exact group. */
  onEditGroup: (groupId: string) => void;
}

/**
 * Group tag of a list row; hovering it (or clicking / Enter / Space) opens the menu of the item's groups.
 * The menu stays open while the pointer is over the tag or the menu, and closes on leave, Esc or outside click.
 */
export function GroupsMenu({ family, groups, onEditGroup }: Readonly<GroupsMenuProps>) {
  const { formatMessage } = useIntl();
  const tagId = useId();
  const { close, floatingHoverProps, open, openByClick, openedByHover, referenceHoverProps } =
    useHoverOpen(GROUPS_MENU_CLOSE_DELAY_MS);

  const { context, floatingStyles, refs } = useFloating({
    middleware: [offset(4), flip({ padding: 5 }), shift({ padding: 5 })],
    onOpenChange: isOpen => !isOpen && close(),
    open,
    placement: 'bottom-end',
    whileElementsMounted: autoUpdate
  });
  const { getFloatingProps, getReferenceProps } = useInteractions([useDismiss(context), useRole(context)]);

  if (groups.length === 0) {
    return null;
  }

  const handleEdit = (groupId: string) => {
    close();
    onEditGroup(groupId);
  };

  return (
    <>
      <button
        aria-label={formatMessage(groupingMessages.showGroups)}
        className="groupsMenu__trigger"
        data-row-action
        ref={refs.setReference}
        type="button"
        {...getReferenceProps({ onClick: openByClick, ...referenceHoverProps })}
      >
        <Tag id={tagId} status="custom" tiny {...getGroupTagStyle(groups)} />
      </button>
      {open && (
        <FloatingPortal>
          {/* Hover must not steal the focus (e.g. from the list search); click / keyboard moves it into the menu */}
          <FloatingFocusManager context={context} disabled={openedByHover} modal={false}>
            <div
              className="groupsMenuPopover"
              ref={refs.setFloating}
              style={floatingStyles}
              {...getFloatingProps(floatingHoverProps)}
            >
              {/* The menu is portaled to <body>, outside the themed app tree */}
              <CustomThemeProvider themeContrast="light">
                <div className="groupsMenu">
                  {groups.length === 1 ? (
                    <SingleGroupCard family={family} group={groups[0]} onEdit={handleEdit} />
                  ) : (
                    <GroupsMenuList family={family} groups={groups} onEdit={handleEdit} />
                  )}
                </div>
              </CustomThemeProvider>
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
}
